import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { retrieveAcknowledgements } from '../../../../common/src/legacy/services/apiRouterService';
import { getI18nLanguageCode } from '../../../../../i18n';
import { Text, VStack, Box, Skeleton } from '@wegroup/design-system';
import AcknowledgementCheckbox from './AcknowledgementCheckbox';
import { AcknowledgementPayload } from '../../../../../types/ProductFactory';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import {
  answerAcknowledgmeent,
  setAcknowledgmeents,
} from '../../store/redux/slices/offersSlice';
import { AcknowledgementsResp } from '../../../../../types/SMT';
import useQueryFetch from '../../../../common/src/hooks/useQueryFetch';
import useSessionId from '../../../../flows/src/hooks/useSessionId';

const AcknowledgementSkeleton: React.FC<
  React.PropsWithChildren<unknown>
> = () => (
  <VStack
    p={{ base: '4', laptop: '8' }}
    bg="white"
    borderRadius="6px"
    border="1px"
    borderColor="gray.50"
    align="stretch"
    spacing="2"
  >
    <Skeleton h="3" />
    <Skeleton h="3" w="50%" />
  </VStack>
);

interface Props {
  riskObjects: string[];
}

const OfferAcknowledgements: React.FC<React.PropsWithChildren<Props>> = ({
  riskObjects,
}) => {
  const { t } = useTranslation();

  return (
    <VStack spacing="14" mt={{ base: '0', laptop: '12' }}>
      {[...riskObjects.sort(), 'general'].map((type) => (
        <AcknowledgementsSection
          key={`OfferAcknowledgements_${type}`}
          title={
            {
              car: t('Questions in regards to the main driver'),
              home: t('Questions in regards to insured property'),
              family: t('Questions in regards to family'),
              legal: t('Questions in regards to legal'),
              general: t('General questions'),
            }[type] || `${t('Questions in regards to ')} ${t(type)}`
          }
          type={type}
        />
      ))}
    </VStack>
  );
};

interface AcknowledgementsSectionProps {
  title: string;
  type: string;
}

const AcknowledgementsSection: React.FC<
  React.PropsWithChildren<AcknowledgementsSectionProps>
> = ({ type }) => {
  const { t } = useTranslation();
  const sessionId = useSessionId();

  const dispatch = useDispatch();
  const acknowledgements = useSelector(
    (state: RootState) => state.offers.offersInfo.acknowledgements,
  );

  const getDefaultValue = (key: string, type: string) => {
    const answeredAck = acknowledgements[type]?.find(
      ({ key: _key }) => _key === key,
    );
    if (answeredAck) return answeredAck.agreed;
  };

  const handleAcknowledgementChange = (
    _acknowledgement: AcknowledgementPayload,
    type: string,
  ) => {
    dispatch(
      answerAcknowledgmeent({
        acknowledgementKey: _acknowledgement.key,
        answer: !!_acknowledgement?.agreed,
        type,
      }),
    );
  };

  const getAcknowledgementType = (type: string) => {
    if (type === 'home') return 'RESIDENCE';
    // GENERAL acknowledgements can not be fetched indivdually
    if (type === 'general') return 'CAR';
    return type.toUpperCase();
  };

  const { data: acknowledgementsData, isLoading } =
    useQueryFetch<AcknowledgementsResp>({
      queryKey: [sessionId, 'acknowledgements', type],
      fetchFn: () =>
        retrieveAcknowledgements(
          getAcknowledgementType(type),
          getI18nLanguageCode().toUpperCase(),
        ),
      errorString: t('Error fetching acknowledgements'),
      options: {
        refetchOnWindowFocus: false,
      },
    });

  useEffect(() => {
    if (acknowledgementsData && !acknowledgements[type]) {
      dispatch(
        setAcknowledgmeents({
          acknowledgements: acknowledgementsData?.[type]?.map(
            ({ key, short }) => ({
              key,
              text: short,
              agreed: undefined,
            }),
          ),
          type,
        }),
      );
    }
  }, [acknowledgementsData]);

  if (isLoading || !acknowledgementsData)
    return (
      <VStack align="stretch" w="100%">
        <Skeleton h="8" maxW="96" mb="6" />
        <VStack spacing="3" align="stretch">
          <AcknowledgementSkeleton />
          <AcknowledgementSkeleton />
          <AcknowledgementSkeleton />
          <AcknowledgementSkeleton />
          <AcknowledgementSkeleton />
        </VStack>
      </VStack>
    );

  return (
    <Box width="100%">
      <Text textStyle="h4Headline" mb="6">
        {
          {
            car: t('Questions in regards to the main driver'),
            home: t('Questions in regards to insured property'),
            family: t('Questions in regards to family'),
            legal: t('Questions in regards to legal'),
            general: t('General questions'),
          }[type]
        }
      </Text>
      <VStack spacing="3">
        {acknowledgementsData?.[type]?.map((ack) => (
          <AcknowledgementCheckbox
            key={`AcknowledgementCheckbox_${ack.key}`}
            data={ack}
            defaultChecked={getDefaultValue(ack.key, type)}
            onChange={(obj) => handleAcknowledgementChange(obj, type)}
          />
        ))}
      </VStack>
    </Box>
  );
};

export default OfferAcknowledgements;
